import React from 'react';
import { Space, Typography } from 'antd';
import { LockOutlined, SafetyCertificateOutlined } from '@ant-design/icons';

const { Text } = Typography;

// Morado de marca de Stripe: se queda hardcodeado a proposito (es la marca, no
// un color de estado) y contrasta bien sobre la superficie clara y la oscura.
const STRIPE_PURPLE = '#635bff';

interface StripeTrustBadgeProps {
  /** Variante compacta: una sola columna, tipografia mas chica. */
  compact?: boolean;
  /** Logo de Stripe a la derecha del texto. */
  showLogo?: boolean;
  style?: React.CSSProperties;
}

/**
 * Aviso de "pagos seguros con Stripe" que estaba copiado en SelectPlan,
 * RedirectingToStripe y SubscriptionManagement. Se extrajo al migrar el dark
 * theme: las tres copias traian el gradiente claro de Stripe hardcodeado y se
 * rompian sobre fondo oscuro.
 */
const StripeTrustBadge: React.FC<StripeTrustBadgeProps> = ({
  compact = false,
  showLogo = false,
  style,
}) => {
  const surface: React.CSSProperties = {
    // `compact` vive dentro de una Card (RedirectingToStripe) y quiere leerse
    // como un recuadro embutido; la variante normal es hermana de otras Cards
    // (SubscriptionManagement) o flota sobre el gradiente de la pagina
    // (SelectPlan), y ahi tiene que leerse como tarjeta.
    background: compact ? 'var(--bg-secondary)' : 'var(--bg-primary)',
    border: '1px solid var(--border-color)',
    borderRadius: compact ? 8 : 12,
    padding: compact ? '12px 16px' : '16px 24px',
    ...style,
  };

  const disclaimer = compact
    ? 'Tus datos están protegidos. Stripe cumple con PCI DSS Nivel 1.'
    : 'Tus datos de pago nunca pasan por nuestros servidores. Stripe cumple con los más altos estándares de seguridad (PCI DSS Nivel 1).';

  const headline = (
    <Text style={{ color: 'var(--text-primary)', fontSize: compact ? 13 : undefined }}>
      {compact ? 'Pagos seguros con ' : 'Pagos procesados de forma segura por '}
      <Text strong style={{ color: STRIPE_PURPLE }}>Stripe</Text>
    </Text>
  );

  const fineprint = (
    <Text type="secondary" style={{ fontSize: compact ? 11 : 12 }}>
      <SafetyCertificateOutlined style={{ marginRight: 4 }} />
      {disclaimer}
    </Text>
  );

  if (compact) {
    return (
      <div style={surface}>
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <Space align="center">
            <LockOutlined style={{ fontSize: 16, color: STRIPE_PURPLE }} />
            {headline}
          </Space>
          {fineprint}
        </Space>
      </div>
    );
  }

  return (
    <div style={surface}>
      <Space align="center" style={{ width: '100%', justifyContent: 'center' }}>
        <LockOutlined style={{ fontSize: 20, color: STRIPE_PURPLE }} />
        <div style={{ textAlign: 'center' }}>
          {headline}
          <br />
          {fineprint}
        </div>
        {showLogo && (
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/b/ba/Stripe_Logo%2C_revised_2016.svg"
            alt="Stripe"
            style={{ height: 24, marginLeft: 8 }}
          />
        )}
      </Space>
    </div>
  );
};

export default StripeTrustBadge;
